import { lenteBarraEscrita, RECUSA_DA_LENTE } from "../../api/lib/lenteSoLeitura";

const API_URL = import.meta.env.VITE_API_URL || "/api";

export class ApiError extends Error {
  // `data` = corpo JSON da resposta de erro (quando há). Deixa o chamador ler
  // campos estruturados além da mensagem — ex.: a { reconciliacao } de um 422 da
  // ingestão de extrato, pra mostrar a divergência e oferecer "importar mesmo assim".
  constructor(public status: number, message: string, public data?: unknown) {
    super(message);
  }
}

// Empresa ativa injetada em toda chamada à API.
// É populada pelo useEmpresaAtiva (hook); fica aqui pra não criar dep circular.
export const EMPRESA_STORAGE_KEY = "m5_empresa_ativa";

/**
 * A EMPRESA É DA ABA, NÃO DO NAVEGADOR.
 *
 * REPRO (torre, 27/07): a aba estava na M5, o dono navegou pra /orcamentos e ela
 * "virou Vitrine sozinha". Não virou — o header é que escorregou. `localStorage`
 * é GLOBAL à origem: quando OUTRA aba troca de empresa, ela sobrescreve a mesma
 * chave, e esta aba — cujo React state ainda é M5, com o formulário cheio de
 * dado da M5 — passa a mandar `X-Empresa-Id` do Vitrine no próximo request.
 * Resultado: POST do Criar v2 no ORC-2026-0033 voltando "orcamento_nao_encontrado",
 * porque o orçamento da M5 realmente não existe no Vitrine.
 *
 * O mesmo escorregão explica o seletor "Pago por (sócio)" listando Carlos/Ana do
 * Vitrine numa despesa da M5: com o header do tenant ERRADO, /socios devolve a
 * lista do outro dono — corretamente, para o header que recebeu. (Uma versão
 * anterior deste comentário culpava também um `get_my_empresas()` fail-open;
 * era falso — sem header, quem tem >1 vínculo enxerga ZERO desde 23/07.)
 *
 * Agora o holder de módulo manda, e ele é por ABA (vive na memória desta página).
 * O localStorage vira o que sempre deveria ter sido: a memória de qual empresa
 * ABRIR no próximo boot — não a fonte da verdade de quem eu sou AGORA.
 */
let _empresaAtivaId: string | null = null;
export function setEmpresaAtivaId(id: string | null) {
  _empresaAtivaId = id;
}
/** A empresa DESTA aba. Usada também pra escopar o cache (ver main.tsx). */
export function empresaAtivaId(): string | null {
  return _empresaAtivaId;
}

function getEmpresaAtivaHeader(): Record<string, string> {
  if (_empresaAtivaId) return { "X-Empresa-Id": _empresaAtivaId };
  // Só até o EmpresaProvider montar (boot). Depois disso o holder sempre vence.
  try {
    const id = typeof window !== "undefined" ? window.localStorage.getItem(EMPRESA_STORAGE_KEY) : null;
    return id ? { "X-Empresa-Id": id } : {};
  } catch {
    return {};
  }
}

// "Ver como" (lente global): quando o admin assume a visão de um usuário, todos
// os GETs carregam ?como=<userId> pro backend escopar (escopoUnidades). Holder de
// módulo setado pelo VerComoProvider — fica aqui pra não criar dep circular. Só
// GET (leitura): writes nunca herdam a lente, pra não agir em nome de terceiros.
let _verComoId: string | null = null;
export function setVerComoId(id: string | null) {
  _verComoId = id;
}
export function comComo(path: string): string {
  if (!_verComoId) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}como=${encodeURIComponent(_verComoId)}`;
}
/** A lente por HEADER (o mesmo que o `apiFetch` manda). Downloads usam junto com `comComo`: arquivo baixado sob a lente
 *  tem o MESMO escopo da tela (Codex P1 #2985: o .ics saía com a empresa inteira enquanto a tela mostrava só o cliente). */
export function cabecalhoDaLente(): Record<string, string> {
  return _verComoId ? { "X-Ver-Como": _verComoId } : {};
}

/**
 * Upload de mídia do WhatsApp via backend (que sobe pro bucket público com
 * service role). Usa multipart — NÃO pode setar Content-Type manualmente
 * (o browser injeta o boundary). Retorna a URL pública permanente.
 */
export async function uploadWhatsappMedia(
  file: File,
  token: string | null
): Promise<{ url: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_URL}/whatsapp/upload`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getEmpresaAtivaHeader(),
    },
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, formatErrorMessage(body));
  return body as { url: string };
}

/** POST multipart genérico (um arquivo no campo `file`). Não seta Content-Type. */
export async function apiUpload<T = unknown>(
  path: string,
  file: File,
  token: string | null,
  /** Campos extras do multipart (ex.: `papel` do anexo). Só string — é form. */
  campos?: Record<string, string>
): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  for (const [k, v] of Object.entries(campos ?? {})) form.append(k, v);
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getEmpresaAtivaHeader(),
    },
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, formatErrorMessage(body));
  return body as T;
}

// Auto-captura de erros (Marcos, 02/07): falha de API vira registro em
// app_errors via endpoint próprio — ninguém precisa tirar print de erro.
// Fire-and-forget: nunca atrapalha o fluxo; dedup por sessão (mesma rota+status
// só reporta 1x) pra não inundar; ignora o próprio endpoint (sem loop) e 401
// (sessão expirada = ruído, não bug).
const ROTA_ERRO_CLIENTE = "/telemetria/erro-cliente";
/**
 * 599 — A RESPOSTA QUE NENHUM SERVIDOR DESTA CASA EMITE.
 *
 * 🟥 24/09: o quadro das 7h acusou "ROTA NOVA — /prospeccao/toques: 1 erro,
 * nunca antes em 14 dias" e derrubou a nota. Era uma VISTORIA AO VIVO de
 * outra sessão: pra testar a tela em produção sem gravar nada, ela intercepta
 * a escrita e devolve um 599 com `interceptado_pela_vistoria`. O cliente não
 * sabia distinguir, reportou como falha de servidor, e a prova de 5xx contou
 * o TESTE como DEFEITO.
 *
 * 🔑 599 fica fora do padrão HTTP (que vai até 511; o Cloudflare usa até 530)
 * e nenhuma rota de `src/api` o devolve — `tests/status-da-vistoria.test.ts`
 * cobra isso, pra que esta exceção nunca esconda um erro de verdade. Mesmo
 * raciocínio do 401 logo abaixo: ruído, não bug.
 */
export const STATUS_DA_VISTORIA = 599;
/**
 * 422 que é DECISÃO, não erro (02/10/2026, Raio-X da TESTES): o servidor devolve o código e a tela abre a
 * escolha (ex.: "prestador_sem_nr" → "Escalar assim mesmo"). Contar isso em `app_errors` fazia o Raio-X
 * acusar defeito onde o fluxo funcionou (o José escalou o Robson 2 s depois, nas duas vezes).
 * Só código que a tela COMPROVADAMENTE trata entra aqui — o resto segue sendo relatado.
 */
export const DECISOES_NAO_SAO_ERRO = new Set(["prestador_sem_nr"]);
const errosJaReportados = new Set<string>();
function reportarErroCliente(
  token: string | null,
  rota: string,
  metodo: string,
  status: number,
  mensagem: string
) {
  try {
    if (rota.startsWith(ROTA_ERRO_CLIENTE) || status === 401 || status === STATUS_DA_VISTORIA) return;
    const chave = `${metodo} ${rota.split("?")[0]} ${status}`;
    if (errosJaReportados.has(chave)) return;
    errosJaReportados.add(chave);
    void fetch(`${API_URL}${ROTA_ERRO_CLIENTE}`, {
      method: "POST",
      // `keepalive`: o relato precisa sobreviver à saída da página. O caso que
      // obrigou isso (29/07) é o erro de VERSÃO NOVA — nós mesmos chamamos
      // `location.reload()` logo depois de reportar, e sem isto o navegador
      // cancelava a requisição no meio: o defeito se consertava e sumia do
      // `app_errors`, deixando o dono cego pra ele. Vale pros outros também
      // (erro em cima de um clique que navega). Corpo é minúsculo, longe do
      // limite de 64 KB do keepalive.
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...getEmpresaAtivaHeader(),
      },
      body: JSON.stringify({
        rota: rota.split("?")[0].slice(0, 200),
        metodo,
        status,
        mensagem: String(mensagem ?? "").slice(0, 500),
      }),
    }).catch(() => {});
  } catch {
    /* reporte nunca pode quebrar a operação do usuário */
  }
}

/**
 * Erro de RENDER (ErrorBoundary) → app_errors, pelo mesmo cano dos erros de
 * API. Antes, exceção de render só mostrava a tela amigável e NINGUÉM ficava
 * sabendo — o dono via o painel "saudável" com tela quebrando pro usuário.
 * Import dinâmico do supabase: evita ciclo e só carrega quando há erro.
 */
export async function reportarErroRender(
  mensagem: string,
  /**
   * "RENDER" = defeito de verdade (500). "VERSAO" = a aba estava velha e pediu
   * um chunk que o deploy substituiu — registra IGUAL (foi assim que o Ruddy
   * descobriu a classe), mas separado, senão a Saúde do Sistema conta deploy
   * como falha e o número mente. Ver `lib/versaoNova`.
   */
  classe: "RENDER" | "VERSAO" = "RENDER",
): Promise<void> {
  try {
    const { supabase } = await import("./supabase");
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token ?? null;
    if (!token) return; // sem sessão o endpoint recusa — seria só ruído
    // 409 (conflito) pra VERSAO: a aba está numa versão diferente da publicada.
    // Precisa ser 4xx/5xx — o validador do endpoint só aceita 400..599.
    const status = classe === "VERSAO" ? 409 : 500;
    reportarErroCliente(token, window.location.pathname || "/", classe, status, mensagem);
  } catch {
    /* telemetria nunca derruba a UI */
  }
}

/**
 * Prazo OPCIONAL, em ms (`init.prazoMs`).
 *
 * 🔴 POR QUE existe: `fetch` não tem prazo nenhum. Uma requisição que fica
 * pendurada não falha — ela simplesmente não volta, e quem deu `await` nela fica
 * esperando pra sempre. No BOOT isso não é lentidão, é morte: `/me/perfil`
 * pendente deixou o painel em "Carregando…" eterno em 28/07, com o console
 * limpo (ver `lib/erroSessao.comPrazo`).
 *
 * É opt-in DE PROPÓSITO: o painel tem ~700 chamadas, e várias são longas por
 * natureza (upload, PDF, IA). Só quem trava a tela inteira enquanto não volta —
 * hoje as duas do boot — pede prazo. O estouro vira `ApiError(408)`, que
 * `deveTentarDeNovo` já sabe repetir.
 */
export interface ApiInit extends RequestInit {
  prazoMs?: number;
}

/**
 * Baixa um ARQUIVO da nossa API (não JSON) já com os headers certos.
 *
 * Mora aqui, junto do `apiFetch`, pelo mesmo motivo do `baixarCsvDoServidor`:
 * quem monta `Authorization` na mão esquece o `X-Empresa-Id`, e quem tem mais
 * de um vínculo passa a receber a resposta de OUTRO tenant — ou nenhuma. O
 * guardrail de `csv-dre-leva-o-tenant` existe justamente pra impedir a terceira
 * cópia desse fetch solta pelo código.
 */
export async function baixarArquivoDaApi(path: string, token: string | null): Promise<Blob> {
  // download é leitura (GET): herda a lente como o apiFetch, por query E por header
  const res = await fetch(`${API_URL}${comComo(path)}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getEmpresaAtivaHeader(),
      ...cabecalhoDaLente(),
    },
  });
  // o corpo do erro segue em `data`: quem chama precisa da frase do 409 (carteira grande), que o arquivo não tem como dizer
  if (!res.ok) throw new ApiError(res.status, `falha_download_${res.status}`, await res.json().catch(() => undefined));
  return res.blob();
}

export async function apiFetch<T = unknown>(
  path: string,
  token: string | null,
  init: ApiInit = {}
): Promise<T> {
  const { prazoMs, ...resto } = init;
  const metodo = (resto.method ?? "GET").toUpperCase();
  const alvoPath = metodo === "GET" ? comComo(path) : path;
  // 🔍 A7/N4 (14/10): sob a lente "ver como" nada é gravado — a tela recusa antes da rede (o servidor recusa também)
  if (lenteBarraEscrita(metodo, `/api${path.split("?")[0]}`, !!_verComoId)) throw new ApiError(403, RECUSA_DA_LENTE.message, RECUSA_DA_LENTE);
  // Sem prazo, nada muda: nenhum controller, nenhum timer, nenhum sinal novo.
  const controle = prazoMs && !resto.signal ? new AbortController() : null;
  const timer = controle ? setTimeout(() => controle.abort(), prazoMs) : null;
  let res: Response;
  try {
    res = await fetch(`${API_URL}${alvoPath}`, {
      ...resto,
      ...(controle ? { signal: controle.signal } : {}),
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...getEmpresaAtivaHeader(),
        // A lente também vai por HEADER (todo método): a Duda é POST e ficava de fora do `?como=`.
        // O servidor só honra onde faz sentido (lib/lente.ts); escritas continuam NÃO herdando a lente.
        ...(_verComoId ? { "X-Ver-Como": _verComoId } : {}),
        ...resto.headers,
      },
    });
  } catch (e) {
    if (controle?.signal.aborted) throw new ApiError(408, "tempo_esgotado");
    throw e;
  } finally {
    if (timer !== null) clearTimeout(timer);
  }

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = formatErrorMessage(body);
    if (!(res.status === 422 && DECISOES_NAO_SAO_ERRO.has(String((body as { error?: unknown })?.error ?? "")))) reportarErroCliente(token, path, metodo, res.status, msg);
    throw new ApiError(res.status, msg, body);
  }
  avisarSeCortou(res, path);
  return body as T;
}

/**
 * TOTAL NUNCA PARCIAL (item 4 do Trilho): quando o servidor corta uma lista no teto ele
 * escreve `X-Lista-Cortada: <mostrados>`. Aqui vira um evento na janela; quem mostra é
 * <AvisoListaCortada/> no Layout — nenhuma página precisa saber. Só listas GET.
 */
export const EVENTO_LISTA_CORTADA = "lista-cortada";
export type ListaCortadaDetalhe = { path: string; mostrados: number };
function avisarSeCortou(res: Response, path: string): void {
  const v = res.headers.get("X-Lista-Cortada");
  if (!v || typeof window === "undefined") return;
  const mostrados = Number(v);
  if (!Number.isFinite(mostrados) || mostrados <= 0) return;
  window.dispatchEvent(new CustomEvent<ListaCortadaDetalhe>(EVENTO_LISTA_CORTADA, { detail: { path: path.split("?")[0], mostrados } }));
}

/**
 * Converte respostas de erro do backend em string legível pelo usuário.
 * Casos cobertos:
 *  - string simples: { error: "missing_token" } → "missing_token"
 *  - ZodError do zValidator: { error: { issues: [...], name: "ZodError" } }
 *    → "email: email inválido; contato_email: email inválido"
 *  - sem error: usa body.message ou "request_failed"
 */
function formatErrorMessage(body: any): string {
  // Backend manda { error: "codigo_tecnico", message: "texto amigavel" } pra erros
  // de negocio (ex: 409 os_com_historico). Prioriza message sempre que existir.
  if (typeof body?.message === "string" && body.message.trim()) return body.message;

  const err = body?.error;
  if (typeof err === "string") return err;

  // ZodError vindo do zValidator
  if (err && typeof err === "object" && Array.isArray(err.issues)) {
    return err.issues
      .map((iss: any) => {
        const campo = Array.isArray(iss.path) && iss.path.length > 0 ? iss.path.join(".") : "campo";
        return `${campo}: ${iss.message}`;
      })
      .join("; ");
  }

  // Fallbacks
  if (err && typeof err === "object") {
    // Última cartada: stringify
    try { return JSON.stringify(err); } catch { return "request_failed"; }
  }
  return "request_failed";
}

export function utils() {
  return {
    money: (v: number) =>
      new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v),
    date: (iso: string) =>
      new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(new Date(iso)),
  };
}
